import PropTypes from 'prop-types';
import {I18n, Translate} from 'react-redux-i18n';
import moment from 'moment-timezone';
import TextField from '@material-ui/core/TextField';
import {InputAdornment} from '@material-ui/core';

import {Icon, TextDatePicker, AbstractFormComponent} from 'front-onceforall-core';
import {requiredValidator} from 'front-onceforall-core/dist/utils/validators';
import {COUNTRY} from 'front-onceforall-core/dist/utils/country';

import {connect} from 'react-redux';

import authenticationService from '../../services/authentication/AuthenticationService';
import projectService from '../../services/project/ProjectService';
import {captureError} from '../../utils/log';

import CountrySelector from '../countrySelector';
import MessageInfo from '../messageInfo';
import {MANAGE_PROJECT_PERMISSION} from '../../utils/constant';

const countries = [
  COUNTRY.FRANCE,
  COUNTRY.GERMANY,
  COUNTRY.ITALY
];

export const ADDITIONAL_NOTES_MAX_LENGTH = 1024;
export const ADDRESS_MAX_LENGTH = 255;
export const INTERNAL_REFERENCE_MAX_LENGTH = 2000;

const POST_CODE_CHECK_WAIT = 500;

export class ProjectDescriptionForm extends AbstractFormComponent {
  constructor(props) {
    super(props);

    this.isBackOffice = authenticationService.roles().isBackOffice();
    this.canManageProject = authenticationService.roles().hasPermission(MANAGE_PROJECT_PERMISSION);
    this.postCodeCheckTimer = null;
  }

  componentDidMount() {
    if (this.props.project) {
      this.checkPostCode();
    }
  }

  getFormData() {
    let formData = {
      name: '',
      internalReference: '',
      addressLine: '',
      city: '',
      postCode: '',
      region: '',
      openingDate: null,
      provisionalAcceptanceDate: null,
      additionalNotes: ''
    };

    if (this.props.project) {
      const {project} = this.props;
      formData = {
        ...formData,
        name: project.name,
        internalReference: project.internalReference,
        addressLine: project.address.addressLine,
        city: project.address.city,
        postCode: project.address.postCode,
        region: project.address.region,
        openingDate: moment(project.openingDate),
        provisionalAcceptanceDate: project.provisionalAcceptanceDate ? moment(project.provisionalAcceptanceDate) : null,
        country: project.country,
        additionalNotes: project.additionalNotes
      };
    } else {
      formData.country = this.props.organisation.country;
    }

    return formData;
  }

  getFormValidators() {
    return {
      name: requiredValidator,
      internalReference: null,
      addressLine: requiredValidator,
      city: requiredValidator,
      postCode: requiredValidator,
      region: null,
      country: requiredValidator,
      openingDate: [requiredValidator, this.validateOpeningIsBeforeInterventionDate],
      provisionalAcceptanceDate: null,
      additionalNotes: null
    };
  }

  validateOpeningIsBeforeInterventionDate = openingDate => {
    if (this.props.oldestInterventionDateSet && !moment(openingDate).isBefore(moment(this.props.oldestInterventionDateSet))) {
      return 'error.dateShouldBeBefore';
    }
    return null;
  };

  handleSubmitCallback() {
    const formData = {...this.state.formData};
    const {addressLine, city, postCode, region, ...project} = formData;
    project.address = {
      addressLine,
      city,
      postCode,
      region
    };
    project.openingDate = project.openingDate.startOf('day').toISOString();
    if (project.provisionalAcceptanceDate) {
      project.provisionalAcceptanceDate = project.provisionalAcceptanceDate.endOf('day').toISOString();
    }

    this.props.onValidate(project);
  }

  checkPostCode = () => {
    const {country, postCode} = this.state.formData;
    if (!postCode || !country) { return; }
    projectService.isPostCodeValid(country, postCode)
      .then(isValid => {
        this.setState(current => ({
          fieldErrors: {
            ...current.fieldErrors,
            postCode: isValid ? null : 'error.validation'
          }
        }));
      })
      .catch(err => {
        captureError('Error checking postCode: ', err);
      });
  };

  handlePostCodeChange = event => {
    clearTimeout(this.postCodeCheckTimer);
    this.handleChange('postCode')(event);
    this.postCodeCheckTimer = setTimeout(this.checkPostCode, POST_CODE_CHECK_WAIT);
  };

  handleCountryChange = event => {
    this.handleChange('country')(event);
    setTimeout(this.checkPostCode, 0);
  };

  render() {
    let editable = true;
    if (this.props.isFromProjectEditView) {
      const isProjectOwner = this.props.organisation?.organisationId === this.props.project?.ownerOrganisation.organisationId;
      editable = this.isBackOffice || (isProjectOwner && this.canManageProject);
    }

    return (
      <form
        onSubmit={this.handleSubmit}
        className="px-0"
      >
        {this.props.title}
        {this.props.notice?.map(
          element => <div
            className="py-4"
            key={element.id}
          >{element}</div>
        )}
        {!editable && <MessageInfo message="message.projectNotEditable"/>}
        {editable && this.props.isFromProjectEditView && (
          <div
            data-id="edit-project-warning"
            className="box warning d-flex align-items-center mb-5"
          >
            <div className="active mr-4">
              <Icon
                icon="warning"
                className="warning"
                size={28}
              />
            </div>
            <div style={{display: 'inline-grid', whiteSpace: 'pre-line', lineHeight: '2rem'}}>
              <div>
                <Translate
                  value="project.edit.warning"
                  dangerousHTML
                />
              </div>
            </div>
          </div>
        )}
        <div>
          <TextField
            label={this.getFieldLabel('project.name', true)}
            error={this.isOnError('name')}
            helperText={this.helperText('name')}
            id="name"
            name="name"
            value={this.state.formData.name}
            onChange={this.handleChange('name')}
            inputProps={{
              maxLength: ADDRESS_MAX_LENGTH
            }}
            disabled={!editable}
            autoFocus
          />
        </div>

        <div>
          <TextField
            label={this.getFieldLabel('project.internalReference')}
            error={this.isOnError('internalReference')}
            helperText={this.helperText('internalReference')}
            id="internalReference"
            name="internalReference"
            value={this.state.formData.internalReference}
            onChange={this.handleChange('internalReference')}
            inputProps={{
              maxLength: INTERNAL_REFERENCE_MAX_LENGTH
            }}
            disabled={!editable}
          />
        </div>

        <div>
          <TextField
            label={this.getFieldLabel('project.address', true)}
            error={this.isOnError('addressLine')}
            helperText={this.helperText('addressLine')}
            id="addressLine"
            name="addressLine"
            multiline
            minRows={3}
            maxRows={20}
            value={this.state.formData.addressLine}
            onChange={this.handleChange('addressLine')}
            disabled={!editable}
            inputProps={{
              maxLength: ADDRESS_MAX_LENGTH
            }}
          />
        </div>

        <div className="row">
          <div className="col-sm-4 col-md-3 col-lg-2">
            <CountrySelector
              value={this.state.formData.country}
              onChange={this.handleCountryChange}
              disabledCountrySelector={this.props.disabledCountrySelector}
              countriesToShow={countries}
            />
          </div>

          <div className="col-sm-8 col-md-9 col-lg-4">
            <div>
              <TextField
                label={this.getFieldLabel('address.region')}
                id="region"
                name="region"
                value={this.state.formData.region}
                onChange={this.handleChange('region')}
                disabled={!editable}
              />
            </div>
          </div>

          <div className="col-sm-4 col-md-3 col-lg-2">
            <div>
              <TextField
                label={this.getFieldLabel('address.postCode', true)}
                error={this.isOnError('postCode', !this.state.formData.postCode)}
                helperText={this.helperText('postCode', !this.state.formData.postCode)}
                id="postCode"
                name="postCode"
                value={this.state.formData.postCode}
                onChange={this.handlePostCodeChange}
                disabled={!editable}
              />
            </div>
          </div>

          <div className="col-sm-8 col-md-9 col-lg-4">
            <div>
              <TextField
                label={this.getFieldLabel('address.city', true)}
                error={this.isOnError('city')}
                helperText={this.helperText('city')}
                id="city"
                name="city"
                value={this.state.formData.city}
                onChange={this.handleChange('city')}
                disabled={!editable}
              />
            </div>
          </div>
        </div>
        <div className="row">
          <div className="col-sm-6 col-md-4 col-xl-3">
            <div>
              <TextDatePicker
                name="openingDate"
                data-id="field-opening-date"
                label={this.getFieldLabel('project.openingDate', true)}
                value={this.state.formData.openingDate}
                onChange={this.handleChange('openingDate')}
                placeholder={I18n.t('datetime.shortDate')}
                helperText={this.helperText('openingDate')}
                error={this.isOnError('openingDate')}
                maxDate={this.props.oldestInterventionDateSet || undefined}
                disabled={!editable}
              />
            </div>
          </div>

          <div className="col-sm-6 col-md-4 col-xl-3">
            <TextDatePicker
              name="provisionalAcceptanceDate"
              data-id="field-provisional-acceptanceDate"
              label={this.getFieldLabel('project.provisionalAcceptanceDate')}
              value={this.state.formData.provisionalAcceptanceDate}
              onChange={this.handleChange('provisionalAcceptanceDate')}
              placeholder={I18n.t('datetime.shortDate')}
              helperText={this.helperText('provisionalAcceptanceDate')}
              error={this.isOnError('provisionalAcceptanceDate')}
            />
          </div>
        </div>

        <TextField
          label={this.getFieldLabel('project.additionalNotes')}
          data-id="field-additional-notes"
          id="additionalNotes"
          name="additionalNotes"
          multiline
          minRows={5}
          value={this.state.formData.additionalNotes || ''}
          onChange={this.handleChange('additionalNotes')}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <span
                  className="information"
                >
                  <Icon
                    icon="info"
                    tooltip={I18n.t('project.additionalNotesInfo')}
                    size={20}
                  />
                </span>
              </InputAdornment>
            )
          }}
          inputProps={{
            maxLength: ADDITIONAL_NOTES_MAX_LENGTH
          }}
        />

        <Translate
          value="message.textCounter"
          counter={this.state.formData.additionalNotes ? this.state.formData.additionalNotes.length : 0}
          maxLength={ADDITIONAL_NOTES_MAX_LENGTH}
        />

        <div className="actions col-12 inline-container space-between">
          {this.props.onCancel ?
            <button
              type="button"
              className="large secondary inline-container"
              onClick={this.props.onCancel}
              data-id="cancel"
            >
              <Icon icon="go-back"/>
              <Translate value="action.cancel"/>
            </button> : <div/>
          }
          <button
            className="large primary inline-container"
            type="submit"
            disabled={this.props.submitted}
          >
            <Icon icon={this.props.submitted ? 'loader' : 'check'}/>
            <Translate value="action.confirm"/>
          </button>
        </div>
      </form>
    );
  }
}

ProjectDescriptionForm.defaultProps = {
  project: null,
  notice: null,
  onCancel: null,
  disabledCountrySelector: false,
  title: null,
  isFromProjectEditView: false
};

ProjectDescriptionForm.propTypes = {
  title: PropTypes.node,
  onValidate: PropTypes.func.isRequired,
  project: PropTypes.object,
  organisation: PropTypes.object.isRequired,
  notice: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.node), PropTypes.node]),
  onCancel: PropTypes.func,
  submitted: PropTypes.bool.isRequired,
  disabledCountrySelector: PropTypes.bool,
  isFromProjectEditView: PropTypes.bool.isRequired,
  oldestInterventionDateSet: PropTypes.string
};

function mapStateToProps(state) {
  return {
    organisation: state.organisation
  };
}

export default connect(mapStateToProps)(ProjectDescriptionForm);
